import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { Skeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, ShieldCheck, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Reviews = () => {
  const [eligible, setEligible] = useState(null);
  const [history, setHistory] = useState([]);
  const [histStats, setHistStats] = useState({ sent_total: 0, sent_today: 0 });
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [auto, setAuto] = useState(null);

  const load = () => {
    api.get("/tools/reviews/eligible").then(({ data }) => setEligible(data));
    api.get("/tools/reviews/history").then(({ data }) => {
      setHistory(data.history);
      setHistStats({ sent_total: data.sent_total || 0, sent_today: data.sent_today || 0 });
    });
    api.get("/tools/reviews/settings").then(({ data }) => setAuto(data.settings));
  };
  useEffect(() => { load(); }, []);

  const saveAuto = async (next) => {
    setAuto(next);
    await api.post("/tools/reviews/settings", { enabled: next.enabled, max_per_day: next.max_per_day });
    toast.success(next.enabled
      ? `Standing instruction ON — up to ${next.max_per_day} review requests will be sent automatically each day`
      : "Automatic review requests turned OFF");
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length < 10 ? [...s, id] : s));

  const send = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const { data } = await api.post("/tools/reviews/send", { order_ids: selected });
      toast.success(`${data.sent} review request(s) sent to Amazon`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  if (!eligible) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-5" data-testid="reviews-tool">
      <div className="bg-muted/60 border border-border rounded-lg p-5 flex items-start gap-4">
        <ShieldCheck className="h-5 w-5 text-green-600 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="text-sm text-muted-foreground space-y-1">
          <p className="font-semibold text-foreground">Yes — review automation is officially supported by Amazon</p>
          <p>This uses Amazon's <strong>Solicitations API</strong> — the same "Request a Review" as Seller Central. Amazon allows <strong>one request per order, 5–30 days after delivery</strong>. Amazon sends the email in the buyer's language; it is fully TOS-compliant.</p>
          <p>Because this <strong>writes to Amazon</strong>, nothing is sent without your explicit approval below (max 10 per batch).</p>
        </div>
      </div>
      {auto && (
        <div className="bg-card border border-border rounded-lg p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="review-auto-card">
          <div className="space-y-1 max-w-xl">
            <p className="font-semibold text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" aria-hidden="true" /> Standing instruction: automatic review requests</p>
            <p className="text-xs text-muted-foreground">
              When ON, the daily sync automatically sends review requests to newly eligible orders (oldest first) up to your daily limit — no further approval needed. Turning this ON is your standing permission for these writes to Amazon.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={String(auto.max_per_day)} onValueChange={(v) => saveAuto({ ...auto, max_per_day: Number(v) })}>
              <SelectTrigger className="w-28 h-9 rounded-full text-xs" data-testid="review-auto-limit"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 20, 30, 50, 100, 250, 500, 1000, 1500].map((n) => <SelectItem key={n} value={String(n)} data-testid={`review-limit-${n}`}>{n} / day</SelectItem>)}</SelectContent>
            </Select>
            <Switch checked={auto.enabled} onCheckedChange={(v) => saveAuto({ ...auto, enabled: v })} data-testid="review-auto-switch" />
          </div>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="eligible-orders">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading text-base font-semibold tracking-tight">Eligible orders ({eligible.orders.length})</h3>
          <Button size="sm" className="rounded-full" disabled={selected.length === 0 || sending}
            onClick={() => setConfirmOpen(true)} data-testid="request-reviews-button">
            <Star className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {sending ? "Sending…" : `Request reviews (${selected.length} selected)`}
          </Button>
        </div>
        {eligible.orders.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No orders currently in the eligible window (roughly 6–32 days after purchase, shipped, not yet requested).</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3"></th>
                {["Order ID", "Purchase Date", "Total", "Fulfillment"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {eligible.orders.map((o) => (
                <tr key={o.order_id} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`eligible-row-${o.order_id}`}>
                  <td className="px-4 py-2.5">
                    <input type="checkbox" checked={selected.includes(o.order_id)} onChange={() => toggle(o.order_id)} data-testid={`review-check-${o.order_id}`} />
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{o.order_id}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{o.purchase_date?.slice(0, 10)}</td>
                  <td className="px-4 py-2.5 font-semibold">{inr(o.total)}</td>
                  <td className="px-4 py-2.5 text-xs">{o.fulfillment === "AFN" ? "FBA" : "Self-ship"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {history.length > 0 && (
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="reviews-history">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-heading text-base font-semibold tracking-tight">Request history</h3>
            <p className="text-xs text-muted-foreground" data-testid="reviews-history-stats">
              <strong className="text-foreground">{histStats.sent_total.toLocaleString("en-IN")}</strong> sent all time · <strong className="text-foreground">{histStats.sent_today}</strong> today
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>{["Order ID", "Purchase Date", "Order Total", "Fulfillment", "Status", "Sent At", "By"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.order_id} className="border-t border-border" data-testid={`history-row-${h.order_id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{h.order_id}</td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">{h.purchase_date?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-2.5 font-semibold">{h.total != null ? inr(h.total) : "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{h.fulfillment === "AFN" ? "FBA" : h.fulfillment === "MFN" ? "Self-ship" : "—"}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={`border-0 font-semibold ${h.status === "sent" ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"}`}>{h.status}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">{new Date(h.sent_at).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{h.sent_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send {selected.length} review request(s) to Amazon?</AlertDialogTitle>
            <AlertDialogDescription>
              Amazon will email each buyer a review request. This action writes to your Amazon account, can't be undone, and each order can only be requested once ever.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reviews-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={send} data-testid="reviews-confirm-send">Yes, send requests</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
